import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

export default function SneakersArticle() {
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Itaobuy Spreadsheet Sneakers: I Modelli Più Ricercati con Foto QC e Prezzi',
    description: 'Scopri le sneakers più ricercate su Itaobuy Spreadsheet: Jordan, Dunk, Yeezy e modelli lifestyle. Guida italiana con foto QC, prezzi e consigli per ogni collezionista.',
    author: { '@type': 'Organization', name: 'Itaobuy Spread' },
    datePublished: '2026-07-14',
    inLanguage: 'it',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: 'Quali sneakers posso trovare su Itaobuy Spreadsheet?', acceptedAnswer: { '@type': 'Answer', text: 'Modelli Jordan, Nike Dunk, Yeezy, New Balance, Asics, Adidas e sneakers lifestyle di tendenza. Ogni paio include foto QC dettagliate da ogni angolazione.' } },
      { '@type': 'Question', name: 'Come verificare l\'autenticità delle sneakers con le foto QC?', acceptedAnswer: { '@type': 'Answer', text: 'Confronta i dettagli chiave: forma della toe box, stitching, posizionamento del logo, colore della suola e qualità dei materiali nelle foto QC ravvicinate.' } },
      { '@type': 'Question', name: 'Itaobuy Spreadsheet è adatto ai collezionisti di sneakers?', acceptedAnswer: { '@type': 'Answer', text: 'Assolutamente sì. Lo spreadsheet organizza le sneakers per modello, collaborazione e rarità, rendendo facile trovare edizioni limitate e modelli da collezione.' } },
    ],
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://itaobuyspread.mom';

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: 'Itaobuy Spreadsheet Sneakers', item: `${siteUrl}/itaobuy-spreadsheet-sneakers` },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <Navbar />
      <main className="pt-20">
        <article className="px-6 md:px-10 max-w-4xl mx-auto py-10 md:py-16">
          <nav className="text-xs text-warm-400 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-tealwarm-600 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-warm-600">Itaobuy Spreadsheet Sneakers</span>
          </nav>

          <header>
            <span className="inline-block px-3 py-1 rounded-full bg-tealwarm-100 text-tealwarm-700 text-xs font-semibold uppercase tracking-wide mb-4">
              Sneakers
            </span>
            <h1 className="font-display text-3xl md:text-5xl font-bold text-warm-950 leading-tight">
              <span className="text-tealwarm-600">Itaobuy Spreadsheet Sneakers:</span> I Modelli Più Ricercati
            </h1>
            <p className="mt-4 text-sm md:text-base text-warm-600 leading-relaxed">
              Per gli appassionati di sneakers, <strong>itaobuy spreadsheet sneakers</strong> è una miniera d&apos;oro. Jordan, Dunk, Yeezy, New Balance — ogni modello è catalogato con foto QC che mostrano forma, materiali e dettagli da ogni angolazione. Che tu sia un collezionista esperto o un nuovo appassionato, qui trovi tutto ciò che ti serve per scegliere il prossimo paio con consapevolezza.
            </p>
          </header>

          <div className="mt-10">
            <img
              src="https://readdy.ai/api/search-image?query=Four%20pairs%20of%20premium%20sneakers%20in%20different%20colorways%20displayed%20on%20tiered%20wooden%20display%20stands%20against%20a%20clean%20concrete%20wall%2C%20soft%20directional%20lighting%20creating%20dramatic%20highlights%20on%20the%20shoe%20silhouettes%2C%20sneakerhead%20collection%20aesthetic%2C%20modern%20urban%20retail%20display%2C%20editorial%20product%20photography%20with%20warm%20neutral%20tones&width=1200&height=600&seq=article-sneakers-hero&orientation=landscape"
              alt="Itaobuy Spreadsheet Sneakers modelli"
              title="Itaobuy Spreadsheet Sneakers"
              className="w-full rounded-2xl"
            />
          </div>

          <section className="mt-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-warm-950">
              I modelli <span className="text-tealwarm-600">sneakers</span> più cercati
            </h2>
            <p className="mt-4 text-sm md:text-base text-warm-700 leading-relaxed">
              L&apos;<strong>itaobuy sheet</strong> organizza le sneakers con una cura maniacale per i dettagli. Ogni scheda prodotto include il nome del modello, la colorway, il prezzo indicativo e — soprattutto — foto QC che mostrano toe box, stitching, logo, suola e dettagli interni. Ecco i modelli più popolari che troverai:
            </p>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { model: 'Air Jordan', note: 'Dalla 1 alla 4, ogni modello con foto QC dettagliate su forma, materiali e logo Jumpman.' },
                { model: 'Nike Dunk', note: 'Low e High, colorway classiche e collaborative. Controlla lo swoosh e la qualità della pelle.' },
                { model: 'Yeezy', note: '350, 700 e slide. Verifica il pattern Primeknit e la forma della suola Boost nelle foto.' },
                { model: 'New Balance', note: 'Modelli 550, 990 e 2002R. Controlla la suede qualche foto QC per valutarne la texture.' },
              ].map((item) => (
                <div key={item.model} className="bg-cream-50 rounded-2xl p-5">
                  <h3 className="font-bold text-warm-900">{item.model}</h3>
                  <p className="mt-1 text-sm text-warm-600 leading-relaxed">{item.note}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-warm-950">
              Checklist per <span className="text-tealwarm-600">sneakers</span> di qualità
            </h2>
            <div className="mt-5 space-y-3">
              {[
                'Toe box: la forma deve essere simmetrica, senza deformazioni o asimmetrie visibili.',
                'Stitching: le cuciture devono essere dritte e uniformi su tutta la scarpa, specialmente intorno allo swoosh.',
                'Materiali: la pelle deve apparire uniforme, la suede vellutata e consistente, la mesh ben tessuta.',
                'Logo e dettagli: verifica il posizionamento preciso dei loghi rispetto alle immagini ufficiali del modello.',
                'Suola: controllo dello spessore, del pattern e della qualità della gomma. Nessuna bolla o imperfezione.',
                'Interno e soletta: le foto QC mostrano anche l\'interno: verifica l\'etichetta della taglia e la qualità della soletta.',
              ].map((tip, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-tealwarm-100 flex items-center justify-center text-tealwarm-700 flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-xs"></i>
                  </div>
                  <p className="text-sm text-warm-700 leading-relaxed">{tip}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-12">
            <div className="bg-warm-50 rounded-2xl p-6 md:p-8">
              <h3 className="font-display text-xl font-bold text-warm-950 flex items-center gap-2">
                <i className="ri-star-line text-tealwarm-600"></i> Consiglio da esperto
              </h3>
              <p className="mt-3 text-sm md:text-base text-warm-700 leading-relaxed">
                Quando esplori <strong>itaobuy spreadsheet sneakers</strong>, inizia sempre dai modelli che conosci meglio. Confronta le foto QC con immagini ufficiali: questo ti aiuta a sviluppare un occhio critico che poi potrai applicare a qualsiasi modello. Con il tempo, diventerai rapidissimo nel riconoscere la qualità a colpo d&apos;occhio.
              </p>
            </div>
          </section>

          <section className="mt-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-warm-950">Domande frequenti</h2>
            <div className="mt-5 space-y-4">
              {[
                { q: 'Quali sneakers posso trovare su Itaobuy Spreadsheet?', a: 'Modelli Jordan, Nike Dunk, Yeezy, New Balance, Asics, Adidas e sneakers lifestyle. Ogni paio include foto QC dettagliate.' },
                { q: 'Come verificare l\'autenticità con le foto QC?', a: 'Confronta la forma della toe box, stitching, posizionamento logo, colore suola e materiali nelle foto QC ravvicinate.' },
                { q: 'Itaobuy Spreadsheet è adatto ai collezionisti?', a: 'Sì! Lo spreadsheet organizza le sneakers per modello, collaborazione e rarità, ideale per i collezionisti.' },
              ].map((item, i) => (
                <div key={i} className="bg-cream-50 rounded-2xl p-5">
                  <h3 className="font-bold text-warm-900">{item.q}</h3>
                  <p className="mt-2 text-sm text-warm-600 leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </section>

          <div className="mt-12 bg-gradient-to-r from-tealwarm-50 to-cream-50 rounded-2xl p-8 text-center">
            <h2 className="font-display text-2xl font-bold text-warm-950">
              Trova le tue <span className="text-tealwarm-600">sneakers</span> ora
            </h2>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com/category-911-Shoes.html" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-warm-900 text-white text-sm font-semibold hover:bg-warm-800 transition-all whitespace-nowrap cursor-pointer">
                Sneakers su Itaobuy Sheet <i className="ri-arrow-right-up-line"></i>
              </a>
              <Link to="/" className="text-sm text-tealwarm-700 hover:text-tealwarm-800 font-medium transition-colors">
                <i className="ri-arrow-left-line mr-1"></i> Torna alla homepage
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}